import { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import MediaLibraryModal from "./MediaLibraryModal";
import CropperModal from "./CropperModal";
import useExternalImportCrop from "./useExternalImportCrop";
import { uploadDroppedMediaFile } from "./mediaLibraryUploadUtils";

const hasActiveMedia = (media) =>
  Boolean(media && (media.mediaUrl || media.thumbnailUrl));

/**
 * Media Library orchestration for form image fields.
 * Click / Replace → open modal. Drag-drop → upload to Media Library → select URL.
 */
export function useMediaLibraryIntegration({
  media = null,
  imageUrl = "",
  onSelect,
  onRemove,
  defaultKeyword = "",
}) {
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [uploading, setUploading] = useState(false);

  const handleSelect = useCallback(
    (selected) => {
      if (typeof onSelect === "function") onSelect(selected);
      setLibraryOpen(false);
    },
    [onSelect]
  );

  const {
    cropSession,
    applyingCrop,
    handleImportForCrop: startCropSession,
    handleCropCancel,
    handleCropComplete,
    isCropOpen,
  } = useExternalImportCrop(handleSelect);

  const openLibrary = useCallback(() => setLibraryOpen(true), []);

  const handleImportForCrop = useCallback(
    (session) => {
      setLibraryOpen(false);
      startCropSession(session);
    },
    [startCropSession]
  );

  const handleFileDrop = useCallback(
    async (file) => {
      if (!file || uploading) return;
      setUploading(true);
      try {
        const uploaded = await uploadDroppedMediaFile(file);
        if (!uploaded) return;
        setRefreshKey((k) => k + 1);
        handleSelect(uploaded);
        toast.success("Image uploaded to Media Library.");
      } finally {
        setUploading(false);
      }
    },
    [uploading, handleSelect]
  );

  const handleRemove = useCallback(() => {
    if (typeof onRemove === "function") onRemove();
  }, [onRemove]);

  const fromMediaLibrary = hasActiveMedia(media);
  const hasImage = Boolean(String(imageUrl || "").trim()) || fromMediaLibrary;

  const uploadEnhancements = useMemo(
    () => ({
      sourceBadge: hasImage ? "media-library" : null,
      onEmptyClick: openLibrary,
      onReplaceClick: openLibrary,
      onFileDrop: handleFileDrop,
      onRemoveClick: handleRemove,
      uploading,
      previewFileName: fromMediaLibrary ? media?.mediaName || "" : "",
      mediaLibraryMode: true,
    }),
    [
      hasImage,
      openLibrary,
      handleFileDrop,
      handleRemove,
      uploading,
      fromMediaLibrary,
      media?.mediaName,
    ]
  );

  const modalSlot = (
    <>
      <MediaLibraryModal
        show={libraryOpen && !isCropOpen}
        onClose={() => setLibraryOpen(false)}
        onSelect={handleSelect}
        onImportForCrop={handleImportForCrop}
        defaultKeyword={defaultKeyword}
        refreshKey={refreshKey}
      />

      <CropperModal
        show={isCropOpen}
        image={cropSession?.cropImage ?? ""}
        fileName={cropSession?.cropFileName ?? "image.png"}
        subtitle="Adjust the crop area, then apply your selection"
        applyLabel="Apply"
        applying={applyingCrop}
        onCancel={handleCropCancel}
        onCropComplete={handleCropComplete}
      />
    </>
  );

  return {
    uploadEnhancements,
    modalSlot,
    openLibrary,
    fromMediaLibrary,
    uploading,
  };
}

export default useMediaLibraryIntegration;
